import React, { useEffect, useState } from "react";
import { Button, Card, Tag, Row, Col, Typography, Space, Statistic, Descriptions, Modal, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import Table from "../../../core/common/dataTable";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchApprovedTemplates } from "../../../core/data/redux/slices/ApprovedTemplatesSlice";
import api from "../../../core/axios/axiosInstance";
import * as XLSX from "xlsx";

const { Title, Text } = Typography;

export default function AdminWhatsappCampaignDetails() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const selectedCampaign = location.state?.record;

  const [campaignData, setCampaignData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const { data: approvedTemplatesData } = useSelector(s => s.approvedTemplates);
  useEffect(() => { dispatch(fetchApprovedTemplates()); }, [dispatch]);

  const fetchCampaign = async () => {
    if (!selectedCampaign?.campaignId) return;
    setLoading(true);
    try {
      const res = await api.post("/superAdmin/whatsapp/campaignsById", { campaignId: selectedCampaign.campaignId });
      if (res.data.success) setCampaignData(res.data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCampaign(); }, [selectedCampaign]);

  const matchedTemplate = approvedTemplatesData?.find(t => t._id === selectedCampaign?.templateId);
  const headerComponent = matchedTemplate?.components?.find(c => c.type === "HEADER");
  const bodyComponent = matchedTemplate?.components?.find(c => c.type === "BODY");
  const footerComponent = matchedTemplate?.components?.find(c => c.type === "FOOTER");
  const buttonsComponent = matchedTemplate?.components?.find(c => c.type === "BUTTONS");
  const headerFormat = headerComponent?.format?.toLowerCase();
  const headerMediaUrl = headerComponent?.media?.s3Url;

  const renderHeader = () => {
    if (!headerComponent) return null;
    if (headerFormat === "text" && headerComponent.text) return <div style={{ fontWeight: 700, marginBottom: 8 }}>{headerComponent.text}</div>;
    if (headerFormat === "image" && headerMediaUrl) return <img src={headerMediaUrl} alt="Header" style={{ width: "100%", borderRadius: 8 }} />;
    if (headerFormat === "video" && headerMediaUrl) return <video src={headerMediaUrl} controls style={{ width: "100%", borderRadius: 8 }} />;
    if (headerFormat === "document") return <div style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>📄 {headerComponent.media?.fileName || "Document"}</div>;
    return null;
  };

  const stats = campaignData?.stats || {};
  const campaign = campaignData?.campaign || selectedCampaign || {};
  const messages = campaignData?.messages || [];

  const mappedMessages = messages.map((msg, i) => ({
    id: msg.metaMessageId || i,
    name: msg.chatName,
    phone: `+${msg.chatNumber}`,
    lastUpdated: msg.deliveredAt ? new Date(msg.deliveredAt).toLocaleString() : msg.sentAt ? new Date(msg.sentAt).toLocaleString() : "N/A",
    status: msg.status,
    sentAt: msg.sentAt,
    deliveredAt: msg.deliveredAt,
    readAt: msg.readAt,
    metaMessageId: msg.metaMessageId,
  }));

  const handleExportCSV = () => {
    if (!campaignData) return;
    const summary = [
      ["Campaign Name", campaign?.campaignName || ""],
      ["Template", campaign?.templateName || ""],
      ["Recipients", stats?.total || 0],
      ["Sent", stats?.sent || 0],
      ["Delivered", stats?.delivered || 0],
      ["Read", stats?.read || 0],
      ["Failed", stats?.failed || 0],
      [],
      ["Contact Name", "Phone", "Status", "Sent At", "Delivered At", "Read At"],
    ];
    const rows = messages.map(msg => [
      msg.chatName || "",
      `+${msg.chatNumber}`,
      msg.status?.toUpperCase() || "",
      msg.sentAt ? new Date(msg.sentAt).toLocaleString() : "",
      msg.deliveredAt ? new Date(msg.deliveredAt).toLocaleString() : "",
      msg.readAt ? new Date(msg.readAt).toLocaleString() : "",
    ]);
    const ws = XLSX.utils.aoa_to_sheet([...summary, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Campaign Details");
    XLSX.writeFile(wb, `${campaign?.campaignName || "campaign"}_details.csv`);
  };

  const columns = [
    { title: "Contact", dataIndex: "name" },
    { title: "Phone", dataIndex: "phone" },
    { title: "Last Updated", dataIndex: "lastUpdated" },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => {
        if (s === "read") return <Tag color="success">Read</Tag>;
        if (s === "delivered") return <Tag color="processing">Delivered</Tag>;
        if (s === "sent") return <Tag color="blue">Sent</Tag>;
        if (s === "failed") return <Tag color="error">Failed</Tag>;
        return <Tag>{s}</Tag>;
      },
    },
    {
      title: "",
      render: (_, record) => (
        <Button type="link" icon={<i className="ti ti-info-circle" />} onClick={() => { setSelectedMessage(record); setIsModalOpen(true); }}>
          More Info
        </Button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="card mt-3">
          <div className="card-body pb-2">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="page-title mb-0">
                <span onClick={() => navigate(-1)} style={{ cursor: "pointer" }}>
                  <i className="ti ti-arrow-narrow-left me-3" />
                </span>
                Campaign Details
              </h4>
              <Space>
                <Tooltip title="Refresh campaign status">
                  <Button icon={<ReloadOutlined />} loading={loading} onClick={fetchCampaign}>
                    Refresh
                  </Button>
                </Tooltip>
                <Button type="primary" className="bg-success" onClick={handleExportCSV}>
                  Export As CSV
                </Button>
              </Space>
            </div>
          </div>
        </div>

        <Row gutter={24}>
          <Col span={16}>
            <Card className="mb-4 bg-transparent">
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {Object.entries(stats).map(([key, value]) => (
                  <Card key={key} className="metric-card outbound-card" hoverable style={{ flex: "0 0 18%" }}>
                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                      <Text type="secondary" className="metric-label fs-2">{value}</Text>
                      <Statistic value={key.charAt(0).toUpperCase() + key.slice(1)} precision={0} className="metric-value" valueStyle={{ color: "#1890ff", fontSize: 12 }} />
                    </Space>
                  </Card>
                ))}
              </div>
            </Card>

            <Card>
              <Table columns={columns} dataSource={mappedMessages} loading={loading} rowKey="id" pagination={false} />
            </Card>
          </Col>

          <Col span={8}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Card>
                <Title level={5} className="ms-0">Campaign Details</Title>
                <Card className="mb-2" size="small"><Text type="secondary">Campaign Name</Text><br /><Text strong>{campaign.campaignName}</Text></Card>
                <Card className="mb-2" size="small"><Text type="secondary">Template</Text><br /><Text strong>{campaign.templateName}</Text></Card>
                <Card className="mb-2" size="small"><Text type="secondary">Recipients</Text><br /><Text strong>{stats.total || 0}</Text></Card>
                <Card size="small">
                  <Text type="secondary">Scheduled At</Text><br />
                  <Text strong>{campaign.scheduledAt ? new Date(campaign.scheduledAt).toLocaleString() : new Date(campaign.createdAt || Date.now()).toLocaleString()}</Text>
                </Card>
              </Card>

              <Card style={{ backgroundImage: "url('/assets/img/wabaBg.png')", backgroundRepeat: "repeat" }}>
                <div style={{ background: "#fff", borderRadius: 12, padding: 14 }}>
                  {matchedTemplate ? (
                    <>
                      {renderHeader()}
                      {bodyComponent?.text && <div style={{ marginBottom: 8 }}>{bodyComponent.text}</div>}
                      {footerComponent?.text && <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>{footerComponent.text}</div>}
                      {buttonsComponent?.buttons?.map((btn, i) => (
                        <Button key={i} block style={{ marginBottom: 6 }}>{btn.text}</Button>
                      ))}
                    </>
                  ) : <Text type="secondary">Template not found</Text>}
                </div>
              </Card>
            </Space>
          </Col>
        </Row>

        <Modal title="Message Details" open={isModalOpen} onCancel={() => { setIsModalOpen(false); setSelectedMessage(null); }} footer={null}>
          {selectedMessage && (
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Phone">{selectedMessage.phone}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={selectedMessage.status === "read" ? "success" : selectedMessage.status === "delivered" ? "processing" : selectedMessage.status === "failed" ? "error" : "default"}>
                  {selectedMessage.status?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Sent At">{selectedMessage.sentAt ? new Date(selectedMessage.sentAt).toLocaleString() : "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Delivered At">{selectedMessage.deliveredAt ? new Date(selectedMessage.deliveredAt).toLocaleString() : "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Read At">{selectedMessage.readAt ? new Date(selectedMessage.readAt).toLocaleString() : "N/A"}</Descriptions.Item>
            </Descriptions>
          )}
        </Modal>
      </div>
    </div>
  );
}
